// src/components/layouts/LayoutPickerSection.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Users } from 'lucide-react';
import { POST_LAYOUTS, POST_LAYOUT_ORDER } from '@/lib/layouts/registry';
import { CORTES_PRESETS } from '@/lib/layouts/cortesPresets';
import { CORTES_PRESET_IDS, resolveCortesPreset } from '@/lib/layouts/resolveCortesPreset';
import { CIRCULO_PRESET_IDS, resolveCirculoPreset } from '@/lib/layouts/resolveCirculoPreset';
import type { PostLayoutConfig } from '@/lib/layouts/types';
import { HorizontalScrollList } from '@/components/HorizontalScrollList';
import { CortesPickerIcon } from './CortesPickerIcon';
import { CirculoPickerIcon } from './CirculoPickerIcon';

export type SelectPostLayoutFn = (
  kind: keyof typeof POST_LAYOUTS,
  options?: { preset?: string },
) => void;

const cardClass = (selected: boolean) =>
  `w-[80px] shrink-0 rounded-lg border-2 p-2 transition-all cursor-pointer ${
    selected
      ? 'border-primary bg-primary/10 shadow-[0_0_12px_-3px] shadow-primary/30'
      : 'border-white/10 hover:border-white/20 bg-muted/20'
  }`;

const LayoutThumb: React.FC<{ kind: string }> = ({ kind }) => {
  if (kind === 'satisfatorio') {
    return (
      <div className="h-[52px] w-full rounded overflow-hidden bg-black/40 mb-1.5 flex flex-col gap-[1px] p-[2px]">
        <div className="flex-1 rounded-sm bg-white/20" />
        <div className="flex-1 rounded-sm bg-emerald-300/25" />
      </div>
    );
  }
  if (kind === 'triple_split') {
    return (
      <div className="h-[52px] w-full rounded overflow-hidden bg-black/40 mb-1.5 flex flex-col gap-[1px] p-[2px]">
        <div className="flex-1 rounded-sm bg-emerald-300/25" />
        <div className="flex-1 rounded-sm bg-white/20" />
        <div className="flex-1 rounded-sm bg-emerald-300/25" />
      </div>
    );
  }
  if (kind === 'fofoca') {
    return (
      <div className="h-[52px] w-full rounded overflow-hidden bg-white mb-1.5 flex flex-col p-[3px] relative">
        <span
          data-testid="post-layout-aspect-fofoca"
          className="absolute right-0.5 top-0.5 z-10 rounded bg-black/75 px-1 py-0.5 text-[7px] font-semibold leading-none text-white"
        >
          9:16
        </span>
        <div className="h-[13px] flex items-center gap-[2px] px-[1px]">
          <div className="h-[7px] w-[7px] rounded-full bg-amber-700/60" />
          <div className="h-[3px] w-[15px] rounded bg-gray-700" />
        </div>
        <div className="flex-1 flex gap-[1px] relative">
          <div className="flex-1 bg-amber-200/50" />
          <div className="flex-1 bg-gray-700/55" />
          <div className="absolute h-[12px] w-[12px] rounded-full bg-black border border-white left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
      </div>
    );
  }
  if (kind === 'fact_quote') {
    return (
      <div className="h-[52px] w-full rounded overflow-hidden bg-black/70 mb-1.5 flex flex-col justify-end p-[4px]">
        <div className="h-[5px] rounded-sm bg-white w-4/5 mb-[3px]" />
        <div className="h-[5px] rounded-sm bg-white/70 w-1/2" />
      </div>
    );
  }
  return (
    <div className="h-[52px] w-full rounded overflow-hidden bg-white mb-1.5 flex flex-col gap-[3px] p-[4px]">
      <div className="flex items-center gap-[3px]">
        <div className="w-[10px] h-[10px] rounded-full bg-gray-300 flex-shrink-0" />
        <div className="flex-1 flex flex-col gap-[2px]">
          <div className="h-[4px] rounded-sm bg-gray-700 w-3/4" />
          <div className="h-[3px] rounded-sm bg-gray-400 w-1/2" />
        </div>
      </div>
      <div className="h-[4px] rounded-sm bg-gray-300 w-full" />
      <div className="flex-1 rounded-sm bg-blue-100 mt-[1px]" />
    </div>
  );
};

export const LayoutPickerSection: React.FC<{
  uniqueSpeakerCount: number;
  /** Which speaker-count layouts to offer (editor: [1]; clips page: [1,2,3,4]). */
  speakerOptions: number[];
  selectedSpeakerLayout: number;
  onSelectSpeakerLayout: (n: number) => void;
  postLayout: PostLayoutConfig | null;
  onSelectPostLayout: SelectPostLayoutFn;
  /** Drop the standalone card chrome when nested in the shared layout card. */
  embedded?: boolean;
}> = ({ uniqueSpeakerCount, speakerOptions, selectedSpeakerLayout, onSelectSpeakerLayout, postLayout, onSelectPostLayout, embedded = false }) => {
  const { t } = useTranslation();
  return (
    <div
      data-testid="multi-head-selector"
      className={embedded ? '' : 'rounded-xl border border-border bg-card mb-4 px-4 py-3'}
    >
      <div className="flex items-center gap-2.5 mb-2.5">
        <div className="p-1 rounded-md bg-primary/15">
          <Users className="w-3.5 h-3.5 text-primary" />
        </div>
        <span className="text-sm font-semibold">
          {t("projectClips.speakerLayout.title")}
        </span>
      </div>
      <HorizontalScrollList>
        {speakerOptions
          .filter((n) => n === 1 || (uniqueSpeakerCount > 1 && n <= uniqueSpeakerCount))
          .map((num) => (
            <button
              key={num}
              type="button"
              data-testid={`multi-head-option-${num}`}
              data-selected={!postLayout && selectedSpeakerLayout === num ? 'true' : 'false'}
              onClick={() => onSelectSpeakerLayout(num)}
              className={cardClass(!postLayout && selectedSpeakerLayout === num)}
            >
              <div className="h-[52px] w-full rounded overflow-hidden bg-black/40 mb-1.5 flex flex-col gap-[1px] p-[2px]">
                {num === 1 && <div className="flex-1 rounded-sm bg-white/20" />}
                {num === 2 && (
                  <>
                    <div className="flex-1 rounded-sm bg-white/20" />
                    <div className="flex-1 rounded-sm bg-white/15" />
                  </>
                )}
                {num === 3 && (
                  <>
                    <div className="flex-1 rounded-sm bg-white/20" />
                    <div className="flex-1 flex gap-[1px]">
                      <div className="flex-1 rounded-sm bg-white/15" />
                      <div className="flex-1 rounded-sm bg-white/10" />
                    </div>
                  </>
                )}
                {num === 4 && (
                  <>
                    <div className="flex-1 flex gap-[1px]">
                      <div className="flex-1 rounded-sm bg-white/20" />
                      <div className="flex-1 rounded-sm bg-white/15" />
                    </div>
                    <div className="flex-1 flex gap-[1px]">
                      <div className="flex-1 rounded-sm bg-white/10" />
                      <div className="flex-1 rounded-sm bg-white/[0.08]" />
                    </div>
                  </>
                )}
              </div>
              <p className="text-[10px] font-medium text-center">
                {num === 1
                  ? t('projectClips.speakerLayout.single')
                  : t('projectClips.speakerLayout.speakers', { count: num })}
              </p>
            </button>
          ))}

        {POST_LAYOUT_ORDER.map((kind) => (
          <button
            key={kind}
            type="button"
            data-testid={`post-layout-option-${kind}`}
            data-selected={postLayout?.kind === kind ? 'true' : 'false'}
            onClick={() => onSelectPostLayout(kind)}
            className={cardClass(postLayout?.kind === kind)}
          >
            <LayoutThumb kind={kind} />
            <p className="text-[10px] font-medium text-center">{t(POST_LAYOUTS[kind].labelKey)}</p>
          </button>
        ))}

        {CORTES_PRESET_IDS.map((preset) => {
          const selected = postLayout?.kind === 'cortes' && resolveCortesPreset(postLayout) === preset;
          const nameKey = CORTES_PRESETS.find((p) => p.id === preset)?.nameKey
            ?? 'projectClips.postLayouts.cortes.label';
          return (
            <button
              key={preset}
              type="button"
              data-testid={`post-layout-option-cortes-${preset}`}
              data-selected={selected ? 'true' : 'false'}
              onClick={() => onSelectPostLayout('cortes', { preset })}
              className={cardClass(selected)}
            >
              <CortesPickerIcon preset={preset} />
              <p className="text-[10px] font-medium text-center">{t(nameKey)}</p>
            </button>
          );
        })}

        {CIRCULO_PRESET_IDS.map((preset) => {
          const selected = postLayout?.kind === 'circulo' && resolveCirculoPreset(postLayout) === preset;
          return (
            <button
              key={preset}
              type="button"
              data-testid={`post-layout-option-circulo-${preset}`}
              data-selected={selected ? 'true' : 'false'}
              onClick={() => onSelectPostLayout('circulo', { preset })}
              className={cardClass(selected)}
            >
              <CirculoPickerIcon preset={preset} />
              <p className="text-[10px] font-medium text-center">
                {t(`projectClips.postLayouts.circulo.${preset}`)}
              </p>
            </button>
          );
        })}
      </HorizontalScrollList>
    </div>
  );
};
